import { FeatureFlagEntry } from 'v5/services/front/FeatureManagementService/types';
import { FeatureManagementActionHandler } from 'v5/services/front/FeatureManagementService/FeatureManagementService';
import {
  applyBulkCellValue,
  configSupportsBulk,
  getEffectiveBooleanValue
} from '../featureFlagBulkActions';
import {
  entrySupportsScope,
  getConfigForScope,
  scopeKeyToSource,
  type FeatureFlagScopeKey
} from './buildFeatureFlagScopeCatalog';

/**
 * Enable/disable the selected flag for Legacy and every workspace (not frontend).
 */
export function bulkSetServerScopes(
  entry: FeatureFlagEntry,
  workspaceColumnIds: readonly number[],
  enabled: boolean,
  onAction: FeatureManagementActionHandler,
  options: { includeLegacy?: boolean } = {}
): void {
  const includeLegacy = options.includeLegacy ?? true;
  if (includeLegacy) {
    applyBulkCellValue(
      onAction,
      'legacy',
      entry.featureId,
      entry.config.legacy,
      enabled
    );
  }

  for (const workspaceId of workspaceColumnIds) {
    applyBulkCellValue(
      onAction,
      { workspaceId },
      entry.featureId,
      entry.config.workspaces[workspaceId] ?? { supported: false },
      enabled
    );
  }
}

export function bulkServerScopesState(
  entry: FeatureFlagEntry,
  workspaceColumnIds: readonly number[],
  options: { includeLegacy?: boolean } = {}
): {
  canBulk: boolean;
  allOn: boolean;
  allOff: boolean;
  onCount: number;
  totalCount: number;
} {
  const includeLegacy = options.includeLegacy ?? true;
  const configs = [
    ...(includeLegacy ? [entry.config.legacy] : []),
    ...workspaceColumnIds.map(
      workspaceId =>
        entry.config.workspaces[workspaceId] ?? { supported: false }
    )
  ].filter(configSupportsBulk);

  if (configs.length === 0) {
    return {
      canBulk: false,
      allOn: false,
      allOff: false,
      onCount: 0,
      totalCount: 0
    };
  }

  const booleans = configs
    .map(getEffectiveBooleanValue)
    .filter((value): value is boolean => value !== null);

  if (booleans.length === 0) {
    return {
      canBulk: true,
      allOn: false,
      allOff: false,
      onCount: 0,
      totalCount: 0
    };
  }

  const onCount = booleans.filter(value => value).length;

  return {
    canBulk: true,
    allOn: onCount === booleans.length,
    allOff: onCount === 0,
    onCount,
    totalCount: booleans.length
  };
}

/**
 * Enable/disable every supported flag for a single source (by-workspace axis).
 */
export function bulkSetSourceFeatures(
  entries: readonly FeatureFlagEntry[],
  scopeKey: FeatureFlagScopeKey,
  enabled: boolean,
  onAction: FeatureManagementActionHandler
): void {
  const source = scopeKeyToSource(scopeKey);
  for (const entry of entries) {
    if (!entrySupportsScope(entry, scopeKey)) {
      continue;
    }
    applyBulkCellValue(
      onAction,
      source,
      entry.featureId,
      getConfigForScope(entry, scopeKey),
      enabled
    );
  }
}

export function bulkSourceFeaturesState(
  entries: readonly FeatureFlagEntry[],
  scopeKey: FeatureFlagScopeKey
): {
  canBulk: boolean;
  allOn: boolean;
  allOff: boolean;
  onCount: number;
  totalCount: number;
} {
  const configs = entries
    .filter(entry => entrySupportsScope(entry, scopeKey))
    .map(entry => getConfigForScope(entry, scopeKey))
    .filter(configSupportsBulk);

  if (configs.length === 0) {
    return {
      canBulk: false,
      allOn: false,
      allOff: false,
      onCount: 0,
      totalCount: 0
    };
  }

  const booleans = configs
    .map(getEffectiveBooleanValue)
    .filter((value): value is boolean => value !== null);

  if (booleans.length === 0) {
    return {
      canBulk: true,
      allOn: false,
      allOff: false,
      onCount: 0,
      totalCount: 0
    };
  }

  const onCount = booleans.filter(value => value).length;

  return {
    canBulk: true,
    allOn: onCount === booleans.length,
    allOff: onCount === 0,
    onCount,
    totalCount: booleans.length
  };
}
